import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import type { SocialPlatform } from "@content-os/config";

export type IdeaStatus = "BACKLOG" | "SELECTED" | "CONVERTED";

export interface Idea {
  id: string;
  title: string;
  description: string;
  platforms: SocialPlatform[];
  formats: string[];
  aiGenerated: boolean;
  status: IdeaStatus;
  createdAt: string;
}

export function useIdeas(brandId: string) {
  return useQuery({
    queryKey: ["ideas", brandId],
    queryFn: () => apiFetch<Idea[]>(`/brands/${brandId}/ideas`),
    enabled: !!brandId,
  });
}

export function useCreateIdea(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Idea>) =>
      apiFetch<Idea>(`/brands/${brandId}/ideas`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ideas", brandId] }),
  });
}

export function useUpdateIdea(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Idea> }) =>
      apiFetch<Idea>(`/brands/${brandId}/ideas/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ideas", brandId] }),
  });
}

export function useDeleteIdea(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/brands/${brandId}/ideas/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ideas", brandId] }),
  });
}

/**
 * Kick off AI idea generation. Returns { jobId, status } — poll the job
 * with useAgentJob and invalidate ["ideas", brandId] on COMPLETED.
 */
export function useGenerateIdeas(brandId: string) {
  return useMutation({
    mutationFn: (input: { topic: string; count?: number }) =>
      apiFetch<{ jobId: string; status: string }>(`/brands/${brandId}/ideas/generate`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
  });
}

export function useConvertIdea(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ projectId: string }>(`/brands/${brandId}/ideas/${id}/convert`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ideas", brandId] });
      qc.invalidateQueries({ queryKey: ["projects", brandId] });
    },
  });
}
